import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas } from '../lib/rutas';
import { Search, Copy, CircleCheck as CheckCircle2, X, Calendar, DollarSign, BarChart3, MessageSquare, Check, MapPin, Truck, Square, SquareCheck as CheckSquare, TrendingUp, TrendingDown, Users, TriangleAlert as AlertTriangle, Phone, ChevronRight, Reply, ShieldCheck, User as UserIcon } from 'lucide-react';
import { useClientRatings } from '../hooks/useClientRatings';
import { ClientRatingBadge } from '../components/ClientRatingBadge';
import ClientDetailModal from '../components/ClientDetailModal';
import CircuitRouteModal from '../components/CircuitRouteModal';
import CircuitRoutesPanel from '../components/CircuitRoutesPanel';
import WhatsAppSendModal from '../components/WhatsAppSendModal';

interface DailyInstallment {
  date: string;
  totalAmount: number;
  customerCount: number;
  installments: {
    id: string;
    amount: number;
    paid_amount?: number;
    status: string;
    installment_number?: number;
    total_installments?: number;
    resumo?: string;
    contract: {
      id: string;
      client_name: string;
      ruta: number;
      phone?: string;
      relative_phones?: { numero: string; relacao: string }[];
      seller_name?: string;
    };
  }[];
}

interface AgendaData {
  [key: string]: {
    [ruta: number]: DailyInstallment;
  };
}

interface MacroDataEntry {
  date: string;
  ruta: number;
  totalReceived: number;
  installmentsReceived: number;
  totalInstallments: number;
  totalValue: number;
}

interface RenewalData {
  ruta: number;
  clientsCount: number;
  estimatedValue: number;
  totalReceivable: number;
  clients: {
    name: string;
    contractValue: number;
    lastInstallmentDate: string;
  }[];
}

interface MacroData {
  totalInstallments: number;
  totalValue: number;
  totalReceived: number;
  installmentsReceived: number;
  dailyDataGrouped: { [date: string]: { [ruta: number]: MacroDataEntry } };
  monthlyRutaSummary: { [ruta: number]: { totalExpected: number; totalReceived: number; totalInstallments: number; installmentsReceived: number } };
  renewalOpportunities: { [ruta: number]: RenewalData };
}

export default function ManagerAgenda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agendaData, setAgendaData] = useState<AgendaData>({});
  const [filteredAgendaData, setFilteredAgendaData] = useState<AgendaData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [showMacroDataModal, setShowMacroDataModal] = useState(false);
  const [macroData, setMacroData] = useState<MacroData | null>(null);
  const [loadingMacroData, setLoadingMacroData] = useState(false);
  const [availableRutas, setAvailableRutas] = useState<number[]>([]);
  const [copiedClientId, setCopiedClientId] = useState<string | null>(null);
  const [editingResumo, setEditingResumo] = useState<string | null>(null);
  const [resumoValues, setResumoValues] = useState<{ [cpf: string]: string }>({});
  const [savingResumo, setSavingResumo] = useState<string | null>(null);
  const [editingResponse, setEditingResponse] = useState<string | null>(null);
  const [responseValues, setResponseValues] = useState<{ [cpf: string]: string }>({});
  const [responseMeta, setResponseMeta] = useState<{ [cpf: string]: { at: string | null; by: string | null } }>({});
  const [savingResponse, setSavingResponse] = useState<string | null>(null);
  const [totalOverdue, setTotalOverdue] = useState<number>(0);
  const [overdueCapital, setOverdueCapital] = useState<number>(0);
  const [overdueLucro, setOverdueLucro] = useState<number>(0);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [totalParcelasMonth, setTotalParcelasMonth] = useState<{ total: number; count: number }>({ total: 0, count: 0 });
  const [totalFromTodayToEnd, setTotalFromTodayToEnd] = useState<{ total: number; count: number }>({ total: 0, count: 0 });
  const [totalReceivedMonth, setTotalReceivedMonth] = useState<{ total: number; count: number }>({ total: 0, count: 0 });
  const [clientDetailContractId, setClientDetailContractId] = useState<string | null>(null);
  const [circuitSelected, setCircuitSelected] = useState<Set<string>>(new Set());
  const [showCircuitModal, setShowCircuitModal] = useState(false);
  const [showCircuitRoutes, setShowCircuitRoutes] = useState(false);
  const [circuitRouteDateFilter, setCircuitRouteDateFilter] = useState<string | undefined>();
  const [existingCircuitStops, setExistingCircuitStops] = useState<Set<string>>(new Set());
  const [cardFilter, setCardFilter] = useState<'all' | 'overdue' | 'toReceive'>('all');
  const [whatsappSelected, setWhatsappSelected] = useState<Set<string>>(new Set());
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);

  const clientIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(filteredAgendaData).forEach(rutas => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(installment => {
          ids.add(installment.contract.id);
        });
      });
    });
    return Array.from(ids);
  }, [filteredAgendaData]);

  const { ratings } = useClientRatings(clientIds);

  useEffect(() => {
    if (user?.role === 'motoboy') {
      navigate('/dashboard/all-clients');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchAvailableRutas();
  }, [user]);

  useEffect(() => {
    fetchAgendaData();
  }, [selectedMonth, selectedRuta, user]);

  useEffect(() => {
    if (user?.role === 'manager') {
      fetchOverdueTotal();
      fetchMonthSummaryTotals();
    }
  }, [user, selectedRuta, selectedMonth]);

  useEffect(() => {
    filterAgendaData();
  }, [searchTerm, agendaData, cardFilter]);

  const fetchOverdueTotal = async () => {
    if (!user?.unit_id) return;

    try {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      const [year, month] = selectedMonth.split('-').map(Number);
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const cutoff = yesterdayStr < monthEnd ? yesterdayStr : monthEnd;

      let overdueQuery = supabase
        .from('installments')
        .select('amount, paid_amount, contract:contracts!inner(ruta, loan_amount, total_amount, approval_status)')
        .eq('unit_id', user.unit_id)
        .neq('contract.approval_status', 'rejected')
        .neq('status', 'paid')
        .gte('due_date', monthStart)
        .lte('due_date', cutoff);

      if (selectedRuta > 0) {
        overdueQuery = overdueQuery.eq('contract.ruta', selectedRuta);
      }

      const { data, error } = await overdueQuery;

      if (error) throw error;

      let total = 0;
      let capital = 0;
      let lucro = 0;
      data?.forEach((inst: any) => {
        const remaining = inst.amount - (inst.paid_amount || 0);
        if (remaining > 0) {
          total += remaining;
          const loanAmount = inst.contract?.loan_amount || 0;
          const totalAmount = inst.contract?.total_amount || 0;
          const capitalRatio = totalAmount > 0 ? loanAmount / totalAmount : 0;
          capital += remaining * capitalRatio;
          lucro += remaining * (1 - capitalRatio);
        }
      });

      setTotalOverdue(total);
      setOverdueCapital(capital);
      setOverdueLucro(lucro);
    } catch (err) {
      console.error('Error fetching overdue total:', err);
    }
  };

  const fetchMonthSummaryTotals = async () => {
    if (!user?.unit_id) return;

    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const todayStr = new Date().toISOString().split('T')[0];

      let summaryQuery = supabase
        .from('installments')
        .select('amount, paid_amount, due_date, contract:contracts!inner(ruta, approval_status)')
        .eq('unit_id', user.unit_id)
        .neq('contract.approval_status', 'rejected')
        .gte('due_date', monthStart)
        .lte('due_date', monthEnd);

      if (selectedRuta > 0) {
        summaryQuery = summaryQuery.eq('contract.ruta', selectedRuta);
      }

      const { data, error } = await summaryQuery;

      if (error) throw error;

      let parcelasTotal = 0;
      let parcelasCount = 0;
      let receivedTotal = 0;
      let receivedCount = 0;
      let fromTodayTotal = 0;
      let fromTodayCount = 0;

      data?.forEach((inst: any) => {
        parcelasTotal += inst.amount;
        parcelasCount += 1;
        if ((inst.paid_amount || 0) > 0) {
          receivedTotal += inst.paid_amount;
          receivedCount += 1;
        }
        const remaining = inst.amount - (inst.paid_amount || 0);
        if (remaining > 0 && inst.due_date >= todayStr) {
          fromTodayTotal += remaining;
          fromTodayCount += 1;
        }
      });

      setTotalParcelasMonth({ total: parcelasTotal, count: parcelasCount });
      setTotalReceivedMonth({ total: receivedTotal, count: receivedCount });
      setTotalFromTodayToEnd({ total: fromTodayTotal, count: fromTodayCount });
    } catch (err) {
      console.error('Error fetching month summary totals:', err);
    }
  };

  const fetchAvailableRutas = async () => {
    if (!user?.unit_id) return;

    try {
      const { data, error } = await supabase.rpc('get_distinct_rutas', { p_unit_id: user.unit_id });

      if (error) throw error;

      const rutas = (data as { ruta: number }[] | null)?.map(r => r.ruta) || [];
      const visible = getVisibleRutas(user);
      setAvailableRutas(rutas.filter(r => visible.includes(r)).sort((a, b) => a - b));
    } catch (err) {
      console.error('Error fetching available rutas:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'manager') fetchExistingCircuitStops();
  }, [selectedMonth, user]);

  const fetchExistingCircuitStops = async () => {
    if (!user?.unit_id) return;
    try {
      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

      const { data } = await supabase
        .from('circuit_routes')
        .select('circuit_route_stops(installment_id)')
        .eq('unit_id', user.unit_id)
        .gte('route_date', startDate)
        .lte('route_date', endDate);

      const stopIds = new Set<string>();
      data?.forEach((route: any) => {
        (route.circuit_route_stops || []).forEach((s: any) => stopIds.add(s.installment_id));
      });
      setExistingCircuitStops(stopIds);
    } catch (err) {
      console.error('Error fetching existing circuit stops:', err);
    }
  };

  const toggleCircuitSelect = (installmentId: string) => {
    setCircuitSelected(prev => {
      const next = new Set(prev);
      if (next.has(installmentId)) next.delete(installmentId);
      else next.add(installmentId);
      return next;
    });
  };

  const toggleSelectAllForDateRuta = (date: string, ruta: number) => {
    const rutas = filteredAgendaData[date];
    if (!rutas || !rutas[ruta]) return;
    const ids = rutas[ruta].installments.map(i => i.id);
    setCircuitSelected(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleWhatsappSelect = (installmentId: string) => {
    setWhatsappSelected(prev => {
      const next = new Set(prev);
      if (next.has(installmentId)) next.delete(installmentId);
      else next.add(installmentId);
      return next;
    });
  };

  const toggleWhatsappSelectAllForDateRuta = (date: string, ruta: number) => {
    const rutas = filteredAgendaData[date];
    if (!rutas || !rutas[ruta]) return;
    const ids = rutas[ruta].installments
      .filter(i => (i.contract.phone || '').replace(/\D/g, '').length >= 10)
      .map(i => i.id);
    setWhatsappSelected(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const whatsappSelectedList = useMemo(() => {
    const rows: { installment_id: string; client_name: string; phone: string; amount: number; due_date: string }[] = [];
    Object.entries(filteredAgendaData).forEach(([date, rutas]) => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(inst => {
          if (whatsappSelected.has(inst.id)) {
            rows.push({
              installment_id: inst.id,
              client_name: inst.contract.client_name,
              phone: inst.contract.phone || '',
              amount: inst.amount - (inst.paid_amount || 0),
              due_date: date,
            });
          }
        });
      });
    });
    return rows;
  }, [filteredAgendaData, whatsappSelected]);

  const getCircuitStopsData = () => {
    const stopsData: { installmentId: string; contractId: string; clientName: string; address: string; phone: string; amount: number; ruta: number; date: string }[] = [];
    Object.entries(filteredAgendaData).forEach(([date, rutas]) => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(inst => {
          if (circuitSelected.has(inst.id)) {
            stopsData.push({
              installmentId: inst.id,
              contractId: inst.contract.id,
              clientName: inst.contract.client_name,
              address: '',
              phone: inst.contract.phone || '',
              amount: inst.amount - (inst.paid_amount || 0),
              ruta: inst.contract.ruta,
              date,
            });
          }
        });
      });
    });
    return stopsData;
  };

  const handleOpenCircuitModal = async () => {
    const stopsData = getCircuitStopsData();
    if (stopsData.length === 0) return;

    const contractIds = [...new Set(stopsData.map(s => s.contractId))];
    const { data: contracts } = await supabase
      .from('contracts')
      .select(`
        id,
        client_name,
        cpf,
        phone,
        relative_phones,
        email,
        address,
        business_address,
        pix_key,
        code,
        loan_amount,
        total_amount,
        created_at,
        ruta,
        installments(id, due_date, amount, paid_amount, status)
      `)
      .in('id', contractIds);

    const contractMap = new Map<string, any>();
    contracts?.forEach((c: any) => contractMap.set(c.id, c));

    stopsData.forEach(s => {
      const contract = contractMap.get(s.contractId);
      if (!contract) return;
      s.address = contract.address || '';

      const balance = (contract.installments || []).reduce((total: number, inst: any) => {
        return total + (inst.amount - (inst.paid_amount || 0));
      }, 0);

      const relPhones = (contract.relative_phones || [])
        .filter((p: any) => p.numero?.trim())
        .map((p: any) => `${p.relacao}: ${p.numero}`)
        .join('\n');

      const parcelas = (contract.installments || [])
        .map((inst: any, idx: number) =>
          `${idx + 1}a Parcela: ${formatCurrency(inst.amount)} - Venc: ${new Date(inst.due_date + 'T00:00:00Z').toLocaleDateString('pt-BR')}`
        )
        .join('\n');

      s.clientDataNotes = `Dados do Cliente:
Nome: ${contract.client_name}
CPF: ${contract.cpf || ''}
Telefone: ${contract.phone || ''}
${relPhones ? `Telefones Parentes:\n${relPhones}` : ''}
Endereco Residencial: ${contract.address || ''}
Endereco Comercial: ${contract.business_address || ''}
Valor Emprestimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Saldo Devedor: ${balance <= 0 ? 'Quitado' : formatCurrency(balance)}
Ruta: ${contract.ruta}
${parcelas ? `\nParcelas:\n${parcelas}` : ''}`.trim();
    });

    setCircuitStopsForModal(stopsData);
    setShowCircuitModal(true);
  };

  const [circuitStopsForModal, setCircuitStopsForModal] = useState<any[]>([]);

  const circuitRouteDate = useMemo(() => {
    const stopsData = getCircuitStopsData();
    const dates = [...new Set(stopsData.map(s => s.date))];
    return dates.length === 1 ? dates[0] : new Date().toISOString().split('T')[0];
  }, [circuitSelected, filteredAgendaData]);

  const fetchMacroData = async () => {
    if (!user) return;

    setLoadingMacroData(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 1);

      let query = supabase
        .from('installments')
        .select(`
          id,
          due_date,
          amount,
          status,
          paid_amount,
          paid_at,
          contract:contracts!installments_contract_id_fkey!inner(
            id,
            client_name,
            ruta,
            approval_status
          )
        `)
        .neq('contract.approval_status', 'rejected')
        .gte('due_date', startDate.toISOString())
        .lt('due_date', endDate.toISOString())
        .eq('unit_id', user?.unit_id);

      if (selectedRuta > 0) {
        query = query.eq('contracts.ruta', selectedRuta);
      }

      const { data: allInstallments, error: installmentsError } = await query
        .order('due_date', { ascending: true });

      if (installmentsError) throw installmentsError;

      const dailyDataMap: { [key: string]: { [ruta: number]: MacroDataEntry } } = {};
      const monthlyRutaTotals: { [ruta: number]: { totalExpected: number; totalReceived: number; totalInstallments: number; installmentsReceived: number } } = {};
      let totalInstallments = 0;
      let totalInstallmentsReceived = 0;
      let totalValue = 0;
      let totalReceived = 0;

      allInstallments?.forEach(installment => {
        if (!installment.contract) return;

        const dateKey = installment.due_date;
        const ruta = installment.contract.ruta;

        if (!monthlyRutaTotals[ruta]) {
          monthlyRutaTotals[ruta] = { totalExpected: 0, totalReceived: 0, totalInstallments: 0, installmentsReceived: 0 };
        }

        totalInstallments += 1;
        monthlyRutaTotals[ruta].totalInstallments += 1;
        monthlyRutaTotals[ruta].totalExpected += installment.amount;

        if (installment.paid_amount && installment.paid_amount > 0) {
          monthlyRutaTotals[ruta].totalReceived += installment.paid_amount;
          totalReceived += installment.paid_amount;
          if (installment.paid_amount >= installment.amount) {
            totalInstallmentsReceived += 1;
            monthlyRutaTotals[ruta].installmentsReceived += 1;
          }
        }

        if (!dailyDataMap[dateKey]) dailyDataMap[dateKey] = {};
        if (!dailyDataMap[dateKey][ruta]) {
          dailyDataMap[dateKey][ruta] = { date: dateKey, ruta, totalReceived: 0, installmentsReceived: 0, totalInstallments: 0, totalValue: 0 };
        }

        totalValue += installment.amount;
        dailyDataMap[dateKey][ruta].totalValue += installment.amount;
        dailyDataMap[dateKey][ruta].totalInstallments += 1;

        if (installment.paid_amount && installment.paid_amount > 0) {
          dailyDataMap[dateKey][ruta].totalReceived += installment.paid_amount;
          if (installment.paid_amount >= installment.amount) {
            dailyDataMap[dateKey][ruta].installmentsReceived += 1;
          }
        }
      });

      const day10Date = new Date(parseInt(year), parseInt(month) - 1, 10);
      const renewalStartDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const day1Str = renewalStartDate.toISOString().split('T')[0];
      const day10Str = day10Date.toISOString().split('T')[0];

      // Buscar todos os contratos que têm alguma parcela vencendo dia 1-10
      let renewalContractsQuery = supabase
        .from('installments')
        .select('contract_id')
        .gte('due_date', day1Str)
        .lte('due_date', day10Str)
        .neq('status', 'paid')
        .eq('unit_id', user?.unit_id);

      const { data: contractsWithDay1to10 } = await renewalContractsQuery;
      const candidateContractIds = [...new Set((contractsWithDay1to10 || []).map(i => i.contract_id))];

      const renewalOpportunitiesMap: { [ruta: number]: RenewalData } = {};

      if (candidateContractIds.length > 0) {
        // Buscar todas as parcelas em aberto desses contratos, junto com dados do contrato
        let allPendingQuery = supabase
          .from('installments')
          .select(`
            id,
            due_date,
            amount,
            paid_amount,
            status,
            contract_id,
            contract:contracts!installments_contract_id_fkey(
              id,
              client_name,
              ruta,
              loan_amount,
              approval_status,
              unit_id
            )
          `)
          .in('contract_id', candidateContractIds)
          .neq('status', 'paid')
          .order('due_date', { ascending: true });

        if (selectedRuta > 0) {
          allPendingQuery = allPendingQuery.eq('contracts.ruta', selectedRuta);
        }

        const { data: allPendingInstallments } = await allPendingQuery;

        // Agrupar por contrato: calcular saldo total em aberto e última parcela em aberto
        const contractPendingMap: {
          [contractId: string]: {
            contract: any;
            totalReceivable: number;
            lastDueDate: string;
          }
        } = {};

        (allPendingInstallments || []).forEach(inst => {
          if (!inst.contract || (inst.contract as any).approval_status === 'rejected') return;
          const remaining = inst.amount - (inst.paid_amount || 0);
          if (remaining <= 0) return;

          if (!contractPendingMap[inst.contract_id]) {
            contractPendingMap[inst.contract_id] = {
              contract: inst.contract,
              totalReceivable: 0,
              lastDueDate: inst.due_date
            };
          }
          contractPendingMap[inst.contract_id].totalReceivable += remaining;
          // Mantém a maior data (última parcela em aberto)
          if (inst.due_date > contractPendingMap[inst.contract_id].lastDueDate) {
            contractPendingMap[inst.contract_id].lastDueDate = inst.due_date;
          }
        });

        // Só entra no mapa de renovação se a ÚLTIMA parcela em aberto cai entre dia 1 e 10
        Object.values(contractPendingMap).forEach(entry => {
          if (entry.lastDueDate < day1Str || entry.lastDueDate > day10Str) return;

          const ruta = entry.contract.ruta;
          if (!renewalOpportunitiesMap[ruta]) {
            renewalOpportunitiesMap[ruta] = { ruta, clientsCount: 0, estimatedValue: 0, totalReceivable: 0, clients: [] };
          }
          renewalOpportunitiesMap[ruta].clientsCount += 1;
          renewalOpportunitiesMap[ruta].totalReceivable += entry.totalReceivable;
          renewalOpportunitiesMap[ruta].estimatedValue += entry.contract.loan_amount || 0;
          renewalOpportunitiesMap[ruta].clients.push({
            name: entry.contract.client_name,
            contractValue: entry.contract.loan_amount || 0,
            lastInstallmentDate: entry.lastDueDate
          });
        });
      }

      setMacroData({
        totalInstallments,
        totalValue,
        totalReceived,
        installmentsReceived: totalInstallmentsReceived,
        dailyDataGrouped: dailyDataMap,
        monthlyRutaSummary: monthlyRutaTotals,
        renewalOpportunities: renewalOpportunitiesMap
      });
    } catch (err) {
      console.error('Error fetching macro data:', err);
      setError('Erro ao carregar dados macro');
    } finally {
      setLoadingMacroData(false);
    }
  };

  const handleShowMacroData = () => {
    setShowMacroDataModal(true);
    fetchMacroData();
  };

  const fetchAgendaData = async () => {
    if (!user) return;

    try {
      const [year, month] = selectedMonth.split('-');
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0);

      const { data: installmentsData, error: installmentsError } = await supabase
        .from('installments')
        .select('id, due_date, amount, status, paid_amount, contract_id, contract:contracts!inner(approval_status)')
        .neq('contract.approval_status', 'rejected')
        .gte('due_date', startDate.toISOString().split('T')[0])
        .lte('due_date', endDate.toISOString().split('T')[0])
        .neq('status', 'paid')
        .eq('unit_id', user?.unit_id)
        .order('due_date', { ascending: true });

      if (installmentsError) throw installmentsError;

      const contractIds = [...new Set(installmentsData?.map(i => i.contract_id).filter(Boolean))];

      const [contractsResult, allContractInstallmentsResult, observationsResult] = await Promise.all([
        contractIds.length > 0
          ? supabase
              .from('contracts')
              .select('id, client_name, ruta, phone, relative_phones, cpf, seller:seller_id(display_name, email)')
              .in('id', contractIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('installments')
          .select('id, contract_id, due_date')
          .in('contract_id', contractIds)
          .order('due_date', { ascending: true }),
        supabase
          .from('client_observations')
          .select('client_id, observation_text, manager_response, manager_response_at, manager_response_by')
          .eq('unit_id', user.unit_id)
      ]);

      if (contractsResult.error) throw contractsResult.error;
      if (allContractInstallmentsResult.error) throw allContractInstallmentsResult.error;

      const observationsMap = new Map<string, string>();
      const responsesMap: { [cpf: string]: string } = {};
      const responseMetaMap: { [cpf: string]: { at: string | null; by: string | null } } = {};
      observationsResult.data?.forEach((obs: any) => {
        observationsMap.set(obs.client_id, obs.observation_text);
        responsesMap[obs.client_id] = obs.manager_response || '';
        responseMetaMap[obs.client_id] = {
          at: obs.manager_response_at || null,
          by: obs.manager_response_by || null,
        };
      });

      const contractMap: { [id: string]: { client_name: string; ruta: number; phone: string; relative_phones: { numero: string; relacao: string }[]; cpf: string; seller?: { display_name: string | null; email: string } | null } } = {};
      contractsResult.data?.forEach((c: any) => { contractMap[c.id] = c; });

      const allContractInstallments = allContractInstallmentsResult.data;

      const installmentNumberMap: { [key: string]: { number: number; total: number } } = {};
      const contractInstallmentCounts: { [key: string]: number } = {};

      allContractInstallments?.forEach(inst => {
        contractInstallmentCounts[inst.contract_id] = (contractInstallmentCounts[inst.contract_id] || 0) + 1;
      });

      allContractInstallments?.forEach(inst => {
        const currentCount = installmentNumberMap[inst.contract_id]?.number || 0;
        installmentNumberMap[inst.id] = { number: currentCount + 1, total: contractInstallmentCounts[inst.contract_id] };
        if (!installmentNumberMap[inst.contract_id]) {
          installmentNumberMap[inst.contract_id] = { number: currentCount + 1, total: 0 };
        } else {
          installmentNumberMap[inst.contract_id].number = currentCount + 1;
        }
      });

      let installments = installmentsData?.map(inst => {
        const contract = contractMap[inst.contract_id];
        const cpf = contract?.cpf || '';
        return {
          id: inst.id,
          due_date: inst.due_date,
          amount: inst.amount,
          status: inst.status,
          paid_amount: inst.paid_amount,
          cpf,
          contract_id: inst.contract_id,
          client_name: contract?.client_name || '',
          ruta: contract?.ruta ?? 1,
          phone: contract?.phone || '',
          relative_phones: (contract?.relative_phones as { numero: string; relacao: string }[]) || [],
          seller_name: contract?.seller?.display_name || contract?.seller?.email?.split('@')[0] || '',
          total_installments: installmentNumberMap[inst.id]?.total || 1,
          installment_number: installmentNumberMap[inst.id]?.number || 1
        };
      });

      const cpfResumoMap: { [cpf: string]: string } = {};
      installments?.forEach(inst => {
        if (inst.cpf && !cpfResumoMap[inst.cpf]) {
          cpfResumoMap[inst.cpf] = observationsMap.get(inst.cpf) || '';
        }
      });
      setResumoValues(prev => ({ ...prev, ...cpfResumoMap }));
      setResponseValues(prev => ({ ...prev, ...responsesMap }));
      setResponseMeta(prev => ({ ...prev, ...responseMetaMap }));

      if (selectedRuta > 0) {
        installments = installments?.filter(inst => inst.ruta === selectedRuta);
      }

      const groupedData: AgendaData = {};

      installments?.forEach((installment: any) => {
        if (installment.status === 'paid' || (installment.paid_amount && installment.paid_amount >= installment.amount)) {
          return;
        }

        const date = new Date(installment.due_date);
        const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        const dateKey = localDate.toISOString().split('T')[0];
        const ruta = installment.ruta;

        if (!groupedData[dateKey]) groupedData[dateKey] = {};
        if (!groupedData[dateKey][ruta]) {
          groupedData[dateKey][ruta] = { date: dateKey, totalAmount: 0, customerCount: 0, installments: [] };
        }

        const remainingAmount = installment.amount - (installment.paid_amount || 0);

        if (remainingAmount > 0) {
          groupedData[dateKey][ruta].totalAmount += remainingAmount;
          groupedData[dateKey][ruta].installments.push({
            id: installment.id,
            amount: installment.amount,
            paid_amount: installment.paid_amount,
            status: installment.status,
            installment_number: installment.installment_number,
            total_installments: installment.total_installments,
            cpf: installment.cpf,
            contract: {
              id: installment.contract_id,
              client_name: installment.client_name,
              ruta: installment.ruta,
              phone: installment.phone,
              relative_phones: installment.relative_phones,
              seller_name: installment.seller_name
            }
          });

          const uniqueCustomers = new Set(groupedData[dateKey][ruta].installments.map(i => i.contract.client_name));
          groupedData[dateKey][ruta].customerCount = uniqueCustomers.size;
        }
      });

      setAgendaData(groupedData);
      setFilteredAgendaData(groupedData);
    } catch (err) {
      console.error('Error fetching agenda data:', err);
      setError('Erro ao carregar dados da agenda');
    } finally {
      setLoading(false);
    }
  };

  const filterAgendaData = () => {
    const today = new Date().toISOString().split('T')[0];
    let filtered: AgendaData = {};

    Object.entries(agendaData).forEach(([date, rutas]) => {
      if (cardFilter === 'overdue' && date >= today) return;
      if (cardFilter === 'toReceive' && date < today) return;

      Object.entries(rutas).forEach(([ruta, data]) => {
        const matchingInstallments = searchTerm
          ? data.installments.filter(installment =>
              installment.contract.client_name.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : data.installments;

        if (matchingInstallments.length > 0) {
          if (!filtered[date]) filtered[date] = {};
          filtered[date][parseInt(ruta)] = {
            ...data,
            installments: matchingInstallments,
            totalAmount: matchingInstallments.reduce((sum, i) => sum + (i.amount - (i.paid_amount || 0)), 0),
            customerCount: new Set(matchingInstallments.map(i => i.contract.client_name)).size
          };
        }
      });
    });

    setFilteredAgendaData(filtered);
  };

  const getFilteredCustomersForDate = (date: string) => {
    if (!agendaData[date]) return [];

    const allCustomers = Object.values(agendaData[date]).flatMap(data =>
      data.installments.map(i => ({
        name: i.contract.client_name,
        ruta: i.contract.ruta,
        amount: i.amount,
        paid_amount: i.paid_amount,
        client_id: i.contract.id
      }))
    );

    if (!modalSearchTerm) return allCustomers;

    return allCustomers.filter(customer =>
      customer.name.toLowerCase().includes(modalSearchTerm.toLowerCase())
    );
  };

  const handleCopyClientData = async (contractId: string, clientName: string) => {
    try {
      const { data: contract, error } = await supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          code,
          loan_amount,
          total_amount,
          created_at,
          ruta
        `)
        .eq('id', contractId)
        .single();

      if (error) throw error;
      if (!contract) return;

      const clientData = `
Dados do Cliente:
Nome: ${contract.client_name}
CPF: ${contract.cpf}
Telefone: ${contract.phone}

Telefones dos Parentes:
${contract.relative_phones.map((phone: any) => `${phone.relacao}: ${phone.numero}`).join('\n')}

Endereço Residencial: ${contract.address}
Endereço Comercial: ${contract.business_address}

Valor do Empréstimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Data: ${new Date(contract.created_at).toLocaleDateString('pt-BR')}
Ruta: ${contract.ruta}`.trim();

      navigator.clipboard.writeText(clientData);
      setCopiedClientId(contractId);
      setTimeout(() => setCopiedClientId(null), 2000);
    } catch (err) {
      console.error('Error copying client data:', err);
    }
  };

  const saveResumo = async (cpf: string) => {
    if (!user || !cpf) return;
    setSavingResumo(cpf);
    try {
      const text = resumoValues[cpf] || '';

      const { data: existing, error: checkError } = await supabase
        .from('client_observations')
        .select('id')
        .eq('client_id', cpf)
        .eq('unit_id', user.unit_id);

      if (checkError) throw checkError;

      if (existing && existing.length > 0) {
        const { error: updateError } = await supabase
          .from('client_observations')
          .update({
            observation_text: text,
            user_id: user.id,
            unit_id: user.unit_id
          })
          .eq('client_id', cpf)
          .eq('unit_id', user.unit_id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('client_observations')
          .insert({
            client_id: cpf,
            observation_text: text,
            user_id: user.id,
            unit_id: user.unit_id
          });
        if (insertError) throw insertError;
      }

      setEditingResumo(null);
    } catch (err) {
      console.error('Error saving resumo:', err);
    } finally {
      setSavingResumo(null);
    }
  };

  const saveManagerResponse = async (cpf: string) => {
    if (!user || !cpf) return;
    setSavingResponse(cpf);
    try {
      const text = (responseValues[cpf] || '').trim();
      const nowIso = new Date().toISOString();

      const { data: existing, error: checkError } = await supabase
        .from('client_observations')
        .select('id')
        .eq('client_id', cpf)
        .eq('unit_id', user.unit_id);

      if (checkError) throw checkError;

      if (existing && existing.length > 0) {
        const { error: updateError } = await supabase
          .from('client_observations')
          .update({
            manager_response: text || null,
            manager_response_at: text ? nowIso : null,
            manager_response_by: text ? user.id : null,
          })
          .eq('client_id', cpf)
          .eq('unit_id', user.unit_id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('client_observations')
          .insert({
            client_id: cpf,
            observation_text: '',
            user_id: user.id,
            unit_id: user.unit_id,
            manager_response: text || null,
            manager_response_at: text ? nowIso : null,
            manager_response_by: text ? user.id : null,
          });
        if (insertError) throw insertError;
      }

      setResponseMeta(prev => ({
        ...prev,
        [cpf]: { at: text ? nowIso : null, by: text ? user.id : null },
      }));
      setEditingResponse(null);
    } catch (err) {
      console.error('Error saving manager response:', err);
    } finally {
      setSavingResponse(null);
    }
  };

  const calculateTotalForMonth = () => {
    let total = 0;
    Object.values(filteredAgendaData).forEach(rutas => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(installment => { total += installment.paid_amount || 0; });
      });
    });
    return total;
  };

  const calculateTotalParcelasForMonth = () => {
    let total = 0; let count = 0;
    Object.values(filteredAgendaData).forEach(rutas => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(installment => { total += installment.amount; count += 1; });
      });
    });
    return { total, count };
  };

  const calculateTotalFromTodayToEndOfMonth = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let total = 0; let count = 0;
    Object.entries(filteredAgendaData).forEach(([dateKey, rutas]) => {
      const date = new Date(dateKey + 'T00:00:00'); date.setHours(0, 0, 0, 0);
      if (date >= today) {
        Object.values(rutas).forEach(data => {
          data.installments.forEach(installment => {
            const remaining = installment.amount - (installment.paid_amount || 0);
            if (remaining > 0) { total += remaining; count += 1; }
          });
        });
      }
    });
    return { total, count };
  };

  const calculateTotalCustomersForMonth = () => {
    const uniqueCustomers = new Set();
    Object.values(filteredAgendaData).forEach(rutas => {
      Object.values(rutas).forEach(data => {
        data.installments.forEach(installment => {
          if ((installment.paid_amount || 0) > 0) uniqueCustomers.add(installment.contract.client_name);
        });
      });
    });
    return uniqueCustomers.size;
  };

  const formatPhone = (phone: string) => {
    const n = phone.replace(/\D/g, '');
    if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return phone;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return localDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getMonthName = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const todayStr = new Date().toISOString().split('T')[0];

  // ─────────────────────────────────────────────
  // LOADING / ERROR STATES
  // ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Carregando agenda...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center max-w-sm">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-24">

      {/* ── HEADER / TOOLBAR ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 lg:p-5">
        <div className="flex flex-col gap-4">
          {/* Title row */}
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Agenda de Recebimentos</h1>
            <p className="text-sm text-gray-400 mt-0.5">Acompanhe cobranças e valores por ruta e data</p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2.5 items-center">
            {/* Ruta select */}
            <select
              value={selectedRuta}
              onChange={(e) => { setSelectedRuta(parseInt(e.target.value)); setCardFilter('all'); }}
              className="h-9 px-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow cursor-pointer"
            >
              <option value={0}>Todas as Rutas</option>
              {availableRutas.map(ruta => (
                <option key={ruta} value={ruta}>Ruta {ruta}</option>
              ))}
            </select>

            {/* Manager-only buttons */}
            {user?.role === 'manager' && (
              <>
                <button
                  onClick={() => { setCircuitRouteDateFilter(undefined); setShowCircuitRoutes(true); }}
                  className="h-9 flex items-center gap-1.5 px-3.5 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  Rotas
                </button>
                <button
                  onClick={handleShowMacroData}
                  className="h-9 flex items-center gap-1.5 px-3.5 text-sm font-medium bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  Dados Macro
                </button>
              </>
            )}

            {/* Search */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar cliente..."
                className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
              />
            </div>

            {/* Month picker */}
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setCardFilter('all'); }}
              className="h-9 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            />
          </div>
        </div>
      </div>

      {/* ── KPI CARDS (manager only) ── */}
      {user?.role === 'manager' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Recebido */}
          <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Mês</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalReceivedMonth.total)}</p>
            <p className="text-sm font-medium text-gray-600 mt-0.5">Total Recebido</p>
            <p className="text-xs text-gray-400 mt-1">{totalReceivedMonth.count} parcelas pagas</p>
          </div>

          {/* Em atraso */}
          <div
            className="group bg-white rounded-2xl border border-red-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer hover:border-red-300 active:scale-[0.98]"
            onClick={() => setShowOverdueModal(true)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Atraso</span>
                <ChevronRight className="w-3.5 h-3.5 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(totalOverdue)}</p>
            <p className="text-sm font-medium text-gray-600 mt-0.5">Total em Atraso</p>
            <p className="text-xs text-gray-400 mt-1">Vencidas até ontem · clique para detalhes</p>
          </div>

          {/* Total parcelas */}
          <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Mês</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalParcelasMonth.total)}</p>
            <p className="text-sm font-medium text-gray-600 mt-0.5">Total de Parcelas</p>
            <p className="text-xs text-gray-400 mt-1">{totalParcelasMonth.count} parcelas no mês</p>
          </div>

          {/* A receber */}
          <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-slate-500" />
              </div>
              <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded-full">Previsto</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalFromTodayToEnd.total)}</p>
            <p className="text-sm font-medium text-gray-600 mt-0.5">A Receber</p>
            <p className="text-xs text-gray-400 mt-1">{totalFromTodayToEnd.count} parcelas de hoje ao fim do mês</p>
          </div>
        </div>
      )}

      {/* ── FILTER BAR (all roles) ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 font-medium">Filtrar:</span>
        <button
          onClick={() => setCardFilter(cardFilter === 'overdue' ? 'all' : 'overdue')}
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
            cardFilter === 'overdue'
              ? 'bg-red-600 text-white border-red-600 shadow-sm'
              : 'bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300'
          }`}
        >
          <AlertTriangle className="w-3 h-3" />
          Em atraso
          {cardFilter === 'overdue' && <X className="w-3 h-3 ml-0.5" />}
        </button>
        <button
          onClick={() => setCardFilter(cardFilter === 'toReceive' ? 'all' : 'toReceive')}
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
            cardFilter === 'toReceive'
              ? 'bg-slate-700 text-white border-slate-700 shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
          }`}
        >
          <TrendingDown className="w-3 h-3" />
          A receber (hoje ao fim)
          {cardFilter === 'toReceive' && <X className="w-3 h-3 ml-0.5" />}
        </button>
      </div>

      {/* ── AGENDA PRINCIPAL ── */}
      <div className="space-y-1">
        {/* Cabeçalho do mês */}
        <div className="flex items-center gap-2 mb-4 px-1">
          <Calendar className="w-4 h-4 text-blue-600" />
          <h2 className="text-base font-semibold text-gray-700 capitalize">{getMonthName(selectedMonth)}</h2>
          {cardFilter !== 'all' && (
            <button
              onClick={() => setCardFilter('all')}
              className={`ml-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
                cardFilter === 'overdue'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cardFilter === 'overdue' ? 'Em atraso' : 'A receber (hoje ao fim)'}
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {Object.keys(filteredAgendaData).length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum recebimento agendado para este mês.</p>
          </div>
        ) : (
          Object.entries(filteredAgendaData)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
            .map(([date, rutas]) => {
              const isToday = date === todayStr;
              const totalDay = Object.values(rutas).reduce((s, d) =>
                s + d.installments.reduce((ss, i) => ss + (i.amount - (i.paid_amount || 0)), 0), 0);
              const totalClients = Object.values(rutas).reduce((s, d) => s + d.customerCount, 0);

              return (
                <div key={date} className="mb-6">
                  {/* Date heading */}
                  <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl mb-3 ${isToday ? 'bg-blue-600' : 'bg-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      {isToday && <span className="w-2 h-2 rounded-full bg-white animate-pulse" />}
                      <h3 className={`text-sm font-semibold capitalize ${isToday ? 'text-white' : 'text-gray-700'}`}>
                        {formatDate(date)}
                        {isToday && <span className="ml-2 text-xs font-normal opacity-80">— Hoje</span>}
                      </h3>
                    </div>
                    <div className={`flex items-center gap-3 text-xs ${isToday ? 'text-blue-100' : 'text-gray-500'}`}>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {totalClients} cliente{totalClients !== 1 ? 's' : ''}
                      </span>
                      {user?.role === 'manager' && (
                        <span className={`font-semibold ${isToday ? 'text-white' : 'text-gray-700'}`}>
                          {formatCurrency(totalDay)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Rota cards grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {availableRutas.map(ruta => {
                      const rutaData = rutas[ruta];
                      const rutaTotal = rutaData
                        ? rutaData.installments.reduce((s, i) => s + (i.amount - (i.paid_amount || 0)), 0)
                        : 0;
                      const allSelectedForRuta = rutaData
                        ? rutaData.installments.every(i => circuitSelected.has(i.id))
                        : false;

                      return (
                        <div
                          key={ruta}
                          className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${rutaData ? 'border-gray-100' : 'border-gray-100 opacity-50'}`}
                        >
                          {/* Rota card header */}
                          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-700">{ruta}</span>
                              </span>
                              <span className="text-sm font-semibold text-gray-700">Ruta {ruta}</span>
                              {user?.role === 'manager' && rutaData && (
                                <button
                                  onClick={() => toggleSelectAllForDateRuta(date, ruta)}
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Selecionar todos para Circuit"
                                >
                                  {allSelectedForRuta
                                    ? <CheckSquare className="w-3.5 h-3.5 text-blue-600" />
                                    : <Square className="w-3.5 h-3.5" />}
                                </button>
                              )}
                              {(user?.role === 'manager' || user?.role === 'seller') && rutaData && (
                                <button
                                  onClick={() => toggleWhatsappSelectAllForDateRuta(date, ruta)}
                                  className="text-gray-400 hover:text-emerald-600 transition-colors"
                                  title="Selecionar todos para envio WhatsApp"
                                >
                                  <MessageSquare className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            {rutaData && (
                              <div className="text-right">
                                {user?.role === 'manager' && (
                                  <p className="text-sm font-bold text-emerald-600">{formatCurrency(rutaTotal)}</p>
                                )}
                                <p className="text-xs text-gray-400">{rutaData.customerCount} cliente{rutaData.customerCount !== 1 ? 's' : ''}</p>
                              </div>
                            )}
                          </div>

                          {/* Installments list */}
                          {rutaData ? (
                            <div className="divide-y divide-gray-50">
                              {rutaData.installments.map(installment => {
                                const remainingAmount = installment.amount - (installment.paid_amount || 0);
                                const clientCpf = installment.cpf || '';
                                const isEditing = editingResumo === clientCpf;
                                const currentResumo = resumoValues[clientCpf] ?? '';
                                const isEditingResponse = editingResponse === clientCpf;
                                const currentResponse = responseValues[clientCpf] ?? '';
                                const responseMetaEntry = responseMeta[clientCpf];
                                const isManager = user?.role === 'manager';
                                const hasResponse = currentResponse.trim().length > 0;
                                const hasPartialPayment = (installment.paid_amount || 0) > 0;
                                const isInCircuit = existingCircuitStops.has(installment.id);
                                const isSelected = circuitSelected.has(installment.id);

                                return (
                                  <div
                                    key={installment.id}
                                    className={`p-3 hover:bg-gray-50/60 transition-colors ${isSelected ? 'bg-blue-50/40' : ''}`}
                                  >
                                    {/* Client name row */}
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <button
                                            onClick={e => { e.stopPropagation(); setClientDetailContractId(installment.contract.id); }}
                                            className="text-sm font-semibold text-gray-800 hover:text-blue-600 transition-colors text-left leading-tight"
                                          >
                                            {installment.contract.client_name}
                                          </button>
                                          <ClientRatingBadge rating={ratings.get(installment.contract.id) || null} />
                                          {installment.installment_number && installment.total_installments && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium leading-none">
                                              {installment.installment_number}/{installment.total_installments}
                                            </span>
                                          )}
                                        </div>
                                        {installment.contract.seller_name && (
                                          <p className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <UserIcon className="w-3 h-3" />
                                            Vendedor: <span className="font-medium text-gray-700">{installment.contract.seller_name}</span>
                                          </p>
                                        )}
                                      </div>

                                      {/* Action buttons */}
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        {user?.role === 'manager' && (
                                          <button
                                            onClick={() => toggleCircuitSelect(installment.id)}
                                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                                              isSelected
                                                ? 'bg-blue-600 text-white'
                                                : isInCircuit
                                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                                : 'bg-gray-100 text-gray-400 hover:bg-blue-100 hover:text-blue-600'
                                            }`}
                                            title={isInCircuit ? 'Ja em rota' : isSelected ? 'Remover' : 'Adicionar ao Circuit'}
                                          >
                                            {isSelected ? <CheckSquare className="w-3.5 h-3.5" />
                                              : isInCircuit ? <MapPin className="w-3.5 h-3.5" />
                                              : <Square className="w-3.5 h-3.5" />}
                                          </button>
                                        )}
                                        {(user?.role === 'manager' || user?.role === 'seller') && installment.contract.phone && (
                                          <button
                                            onClick={() => toggleWhatsappSelect(installment.id)}
                                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                                              whatsappSelected.has(installment.id)
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-gray-100 text-gray-400 hover:bg-emerald-100 hover:text-emerald-600'
                                            }`}
                                            title={whatsappSelected.has(installment.id) ? 'Remover do envio WhatsApp' : 'Selecionar para envio WhatsApp'}
                                          >
                                            <MessageSquare className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleCopyClientData(installment.contract.id, installment.contract.client_name)}
                                          className="w-7 h-7 rounded-lg bg-gray-700 text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                                          title="Copiar dados"
                                        >
                                          {copiedClientId === installment.contract.id
                                            ? <CheckCircle2 className="w-3.5 h-3.5" />
                                            : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                        <DollarSign className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                      </div>
                                    </div>

                                    {/* Phone links */}
                                    {installment.contract.phone && (
                                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 mb-2">
                                        <a
                                          href={`https://wa.me/55${installment.contract.phone.replace(/\D/g, '')}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={e => e.stopPropagation()}
                                          className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 hover:underline"
                                        >
                                          <Phone className="w-3 h-3" />
                                          {formatPhone(installment.contract.phone)}
                                        </a>
                                        {installment.contract.relative_phones?.filter(p => p.numero?.trim()).map((p, i) => (
                                          <span key={i} className="inline-flex items-center gap-1">
                                            <a
                                              href={`https://wa.me/55${p.numero.replace(/\D/g, '')}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={e => e.stopPropagation()}
                                              className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline"
                                            >
                                              {formatPhone(p.numero)}
                                            </a>
                                            {p.relacao && (
                                              <span className="text-xs text-gray-400">({p.relacao})</span>
                                            )}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Financial info */}
                                    {user?.role === 'manager' ? (
                                      <div className="flex items-center gap-3 mb-2">
                                        <div>
                                          <p className="text-xs text-gray-400">Parcela</p>
                                          <p className="text-xs font-semibold text-gray-700">{formatCurrency(installment.amount)}</p>
                                        </div>
                                        {hasPartialPayment && (
                                          <div>
                                            <p className="text-xs text-gray-400">Pago</p>
                                            <p className="text-xs font-semibold text-emerald-600">{formatCurrency(installment.paid_amount!)}</p>
                                          </div>
                                        )}
                                        {remainingAmount > 0 && (
                                          <div>
                                            <p className="text-xs text-gray-400">Restante</p>
                                            <p className="text-xs font-semibold text-blue-600">{formatCurrency(remainingAmount)}</p>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-3 mb-2">
                                        <div>
                                          <p className="text-xs text-gray-400">Parcela</p>
                                          <p className="text-xs font-semibold text-gray-700">{formatCurrency(installment.amount)}</p>
                                        </div>
                                        {hasPartialPayment && (
                                          <div>
                                            <p className="text-xs text-gray-400">Pago</p>
                                            <p className="text-xs font-semibold text-emerald-600">{formatCurrency(installment.paid_amount!)}</p>
                                          </div>
                                        )}
                                        {remainingAmount > 0 && (
                                          <div>
                                            <p className="text-xs text-gray-400">Restante</p>
                                            <p className="text-xs font-semibold text-blue-600">{formatCurrency(remainingAmount)}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Resumo */}
                                    <div>
                                      {isEditing ? (
                                        <div className="flex gap-1.5 items-start">
                                          <textarea
                                            autoFocus
                                            value={currentResumo}
                                            onChange={(e) => setResumoValues(prev => ({ ...prev, [clientCpf]: e.target.value }))}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveResumo(clientCpf); }
                                              if (e.key === 'Escape') setEditingResumo(null);
                                            }}
                                            placeholder="Escreva um resumo..."
                                            className="flex-1 text-xs border border-blue-300 rounded-lg px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                            rows={2}
                                          />
                                          <button
                                            onClick={() => saveResumo(clientCpf)}
                                            disabled={savingResumo === clientCpf}
                                            className="w-7 h-7 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors flex-shrink-0 mt-0.5"
                                          >
                                            {savingResumo === clientCpf
                                              ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                                              : <Check className="w-3 h-3" />}
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => setEditingResumo(clientCpf)}
                                          className={`w-full text-left text-xs rounded-lg px-2.5 py-1.5 transition-colors flex items-start gap-1.5 ${
                                            currentResumo
                                              ? 'text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100'
                                              : 'text-gray-400 bg-gray-50 border border-dashed border-gray-200 hover:bg-gray-100'
                                          }`}
                                        >
                                          <MessageSquare className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                          <span className="whitespace-pre-wrap break-words">{currentResumo || 'Adicionar resumo...'}</span>
                                        </button>
                                      )}
                                    </div>

                                    {/* Manager response */}
                                    {(isManager || hasResponse) && (
                                      <div className="mt-1.5">
                                        {isEditingResponse && isManager ? (
                                          <div className="flex gap-1.5 items-start">
                                            <textarea
                                              autoFocus
                                              value={currentResponse}
                                              onChange={(e) => setResponseValues(prev => ({ ...prev, [clientCpf]: e.target.value }))}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveManagerResponse(clientCpf); }
                                                if (e.key === 'Escape') setEditingResponse(null);
                                              }}
                                              placeholder="Responder ou questionar o resumo..."
                                              className="flex-1 text-xs border border-teal-300 rounded-lg px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                                              rows={2}
                                            />
                                            <button
                                              onClick={() => saveManagerResponse(clientCpf)}
                                              disabled={savingResponse === clientCpf}
                                              className="w-7 h-7 bg-teal-600 text-white rounded-lg flex items-center justify-center hover:bg-teal-700 transition-colors flex-shrink-0 mt-0.5"
                                              title="Salvar resposta"
                                            >
                                              {savingResponse === clientCpf
                                                ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                                                : <Check className="w-3 h-3" />}
                                            </button>
                                          </div>
                                        ) : hasResponse ? (
                                          isManager ? (
                                            <button
                                              onClick={() => setEditingResponse(clientCpf)}
                                              className="w-full text-left text-xs rounded-lg px-2.5 py-1.5 transition-colors flex items-start gap-1.5 text-teal-800 bg-teal-50 border border-teal-200 hover:bg-teal-100"
                                              title={responseMetaEntry?.at ? `Resposta do gestor - ${new Date(responseMetaEntry.at).toLocaleString('pt-BR')}` : 'Resposta do gestor'}
                                            >
                                              <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                              <span className="whitespace-pre-wrap break-words flex-1">
                                                <span className="font-semibold">Gestor:</span> {currentResponse}
                                              </span>
                                            </button>
                                          ) : (
                                            <div
                                              className="w-full text-xs rounded-lg px-2.5 py-1.5 flex items-start gap-1.5 text-teal-800 bg-teal-50 border border-teal-200"
                                              title={responseMetaEntry?.at ? `Resposta do gestor - ${new Date(responseMetaEntry.at).toLocaleString('pt-BR')}` : 'Resposta do gestor'}
                                            >
                                              <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                              <span className="whitespace-pre-wrap break-words flex-1">
                                                <span className="font-semibold">Gestor:</span> {currentResponse}
                                              </span>
                                            </div>
                                          )
                                        ) : isManager ? (
                                          <button
                                            onClick={() => setEditingResponse(clientCpf)}
                                            className="w-full text-left text-xs rounded-lg px-2.5 py-1.5 transition-colors flex items-start gap-1.5 text-gray-400 bg-gray-50 border border-dashed border-gray-200 hover:bg-gray-100"
                                          >
                                            <Reply className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                            <span>Responder / questionar como gestor...</span>
                                          </button>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="px-4 py-6 text-center">
                              <p className="text-xs text-gray-400">Nenhum recebimento</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
        )}
      </div>

      {/* ── CIRCUIT SELECTION FOOTER BAR ── */}
      {user?.role === 'manager' && circuitSelected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-4 py-3 shadow-2xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {circuitSelected.size} cliente{circuitSelected.size !== 1 ? 's' : ''} selecionado{circuitSelected.size !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-400">Prontos para criar rota no Circuit</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCircuitSelected(new Set())}
                className="h-9 px-4 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors"
              >
                Limpar
              </button>
              <button
                onClick={handleOpenCircuitModal}
                className="h-9 px-4 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              >
                <MapPin className="w-4 h-4" />
                Criar Rota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── WHATSAPP SELECTION FOOTER BAR ── */}
      {whatsappSelected.size > 0 && (
        <div
          className={`fixed left-0 right-0 z-40 bg-emerald-900/95 backdrop-blur-sm border-t border-emerald-700 px-4 py-3 shadow-2xl ${
            user?.role === 'manager' && circuitSelected.size > 0 ? 'bottom-[68px]' : 'bottom-0'
          }`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {whatsappSelected.size} cliente{whatsappSelected.size !== 1 ? 's' : ''} para WhatsApp
                </p>
                <p className="text-xs text-emerald-200">Escolha o template e envie em lote</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWhatsappSelected(new Set())}
                className="h-9 px-4 text-sm font-medium text-emerald-100 bg-emerald-800/60 hover:bg-emerald-800 rounded-xl transition-colors"
              >
                Limpar
              </button>
              <button
                onClick={() => setShowWhatsappModal(true)}
                className="h-9 px-4 text-sm font-medium bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors flex items-center gap-1.5"
              >
                <MessageSquare className="w-4 h-4" />
                Enviar mensagem
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── WHATSAPP SEND MODAL ── */}
      <WhatsAppSendModal
        isOpen={showWhatsappModal}
        onClose={() => setShowWhatsappModal(false)}
        selected={whatsappSelectedList}
        onSent={(res) => {
          if (res?.total_sent > 0 || res?.total_failed > 0 || res?.total_skipped > 0) {
            setWhatsappSelected(new Set());
          }
        }}
      />

      {/* ── CIRCUIT ROUTE MODAL ── */}
      {showCircuitModal && circuitStopsForModal.length > 0 && (
        <CircuitRouteModal
          stops={circuitStopsForModal}
          routeDate={circuitRouteDate}
          onClose={() => setShowCircuitModal(false)}
          onSuccess={() => {
            setCircuitSelected(new Set());
            setCircuitStopsForModal([]);
            fetchExistingCircuitStops();
          }}
        />
      )}

      {/* ── CIRCUIT ROUTES PANEL ── */}
      {showCircuitRoutes && (
        <CircuitRoutesPanel
          routeDate={circuitRouteDateFilter}
          onClose={() => setShowCircuitRoutes(false)}
        />
      )}

      {/* ── CLIENT DETAIL MODAL ── */}
      {clientDetailContractId && (
        <ClientDetailModal
          contractId={clientDetailContractId}
          onClose={() => setClientDetailContractId(null)}
        />
      )}

      {/* ── OVERDUE BREAKDOWN MODAL ── */}
      {showOverdueModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowOverdueModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">Detalhamento do Atraso</h2>
                <p className="text-xs text-gray-400 capitalize mt-0.5">{getMonthName(selectedMonth)} · parcelas vencidas até ontem</p>
              </div>
              <button
                onClick={() => setShowOverdueModal(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Total em Atraso */}
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <p className="text-xs font-medium text-red-500 uppercase tracking-wide mb-1">Total em Atraso</p>
                <p className="text-3xl font-bold text-red-600">{formatCurrency(totalOverdue)}</p>
                <p className="text-xs text-red-400 mt-1">Valor total das parcelas vencidas ainda não recebidas</p>
              </div>

              {/* Composition bar */}
              {totalOverdue > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Capital</span>
                    <span>Lucro</span>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden bg-gray-100 flex">
                    <div
                      className="h-full bg-orange-500 transition-all"
                      style={{ width: `${(overdueCapital / totalOverdue) * 100}%` }}
                    />
                    <div
                      className="h-full bg-amber-400 transition-all"
                      style={{ width: `${(overdueLucro / totalOverdue) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{((overdueCapital / totalOverdue) * 100).toFixed(1)}%</span>
                    <span>{((overdueLucro / totalOverdue) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              )}

              {/* Prejuizo Real */}
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Prejuizo Real</p>
                </div>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(overdueCapital)}</p>
                <p className="text-xs text-orange-400 mt-1">Capital emprestado que saiu da empresa e ainda não voltou</p>
              </div>

              {/* Lucro Perdido */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Lucro Perdido</p>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(overdueLucro)}</p>
                <p className="text-xs text-amber-400 mt-1">Juros que seriam recebidos mas ainda estão em aberto</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MACRO DATA MODAL ── */}
      {showMacroDataModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-6 py-4 z-10 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Dados Macro</h2>
                <p className="text-xs text-gray-400 capitalize">{getMonthName(selectedMonth)}</p>
              </div>
              <button
                onClick={() => setShowMacroDataModal(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {loadingMacroData ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : macroData ? (
                <div className="space-y-8">
                  {/* Overall Summary */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                      <p className="text-xs font-medium text-blue-600 mb-1">Total de Parcelas</p>
                      <p className="text-2xl font-bold text-blue-700">{macroData.totalInstallments}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                      <p className="text-xs font-medium text-slate-600 mb-1">Valor Total</p>
                      <p className="text-2xl font-bold text-slate-700">{formatCurrency(macroData.totalValue)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                      <p className="text-xs font-medium text-emerald-600 mb-1">Parcelas Pagas</p>
                      <p className="text-2xl font-bold text-emerald-700">{macroData.installmentsReceived}</p>
                      <p className="text-xs text-emerald-600 mt-0.5">
                        {macroData.totalInstallments > 0 ? ((macroData.installmentsReceived / macroData.totalInstallments) * 100).toFixed(1) : 0}% do total
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
                      <p className="text-xs font-medium text-green-600 mb-1">Valor Recebido</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(macroData.totalReceived)}</p>
                      <p className="text-xs text-green-600 mt-0.5">
                        {macroData.totalValue > 0 ? ((macroData.totalReceived / macroData.totalValue) * 100).toFixed(1) : 0}% do total
                      </p>
                    </div>
                  </div>

                  {/* Renewal Opportunities */}
                  {Object.keys(macroData.renewalOpportunities).length > 0 && (
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-5 h-5 text-teal-600" />
                        <h3 className="font-semibold text-gray-800">Oportunidades de Renovacao (Dia 1-10)</h3>
                      </div>
                      <p className="text-xs text-gray-500 mb-5">
                        Clientes cuja ultima parcela vence entre os dias 1 e 10 — candidatos ideais para renovacao.
                      </p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
                        {availableRutas.map(ruta => (
                          <div key={ruta} className="bg-white rounded-xl p-3 shadow-sm text-center">
                            <p className="text-xs font-semibold text-gray-500 mb-2">Ruta {ruta}</p>
                            {macroData.renewalOpportunities[ruta] ? (
                              <div className="space-y-1.5">
                                <div>
                                  <p className="text-xl font-bold text-teal-600">{macroData.renewalOpportunities[ruta].clientsCount}</p>
                                  <p className="text-xs text-gray-400">clientes</p>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-blue-700">{formatCurrency(macroData.renewalOpportunities[ruta].totalReceivable)}</p>
                                  <p className="text-xs text-gray-400">a receber</p>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-teal-700">{formatCurrency(macroData.renewalOpportunities[ruta].estimatedValue)}</p>
                                  <p className="text-xs text-gray-400">estimado</p>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 py-2">—</p>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-teal-100/60 border border-teal-200 rounded-xl px-4 py-3 flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-teal-600 flex-shrink-0" />
                        <div className="text-sm space-y-0.5">
                          <p className="font-medium text-teal-800">
                            {Object.values(macroData.renewalOpportunities).reduce((s, r) => s + r.clientsCount, 0)} clientes no total
                          </p>
                          <p className="text-teal-700">
                            A receber (Dia 1-10): <span className="font-semibold">{formatCurrency(Object.values(macroData.renewalOpportunities).reduce((s, r) => s + r.totalReceivable, 0))}</span>
                          </p>
                          <p className="text-teal-700">
                            Estimado em renovacoes: <span className="font-semibold">{formatCurrency(Object.values(macroData.renewalOpportunities).reduce((s, r) => s + r.estimatedValue, 0))}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Data */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-800 mb-4">Dados Diarios por Ruta</h3>
                    <div className="space-y-4">
                      {Object.entries(macroData.dailyDataGrouped)
                        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                        .map(([date, rutasData]) => (
                          <div key={date} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                              <h4 className="text-sm font-semibold text-gray-700 capitalize">{formatDate(date)}</h4>
                            </div>
                            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                              {availableRutas.map(ruta => (
                                <div key={ruta} className="bg-gray-50 rounded-xl p-3 text-center">
                                  <p className="text-xs font-semibold text-gray-500 mb-2">Ruta {ruta}</p>
                                  {rutasData[ruta] ? (
                                    <div className="space-y-1.5">
                                      <div>
                                        <p className="text-sm font-bold text-blue-600">{rutasData[ruta].installmentsReceived}/{rutasData[ruta].totalInstallments}</p>
                                        <p className="text-xs text-gray-400">parcelas</p>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                          className="bg-emerald-500 h-1.5 rounded-full"
                                          style={{ width: `${rutasData[ruta].totalValue > 0 ? (rutasData[ruta].totalReceived / rutasData[ruta].totalValue) * 100 : 0}%` }}
                                        />
                                      </div>
                                      <p className="text-xs font-semibold text-emerald-600">{formatCurrency(rutasData[ruta].totalReceived)}</p>
                                      <p className="text-xs text-orange-500">{formatCurrency(rutasData[ruta].totalValue - rutasData[ruta].totalReceived)} pend.</p>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400 py-2">—</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Monthly Ruta Summary */}
                  <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                      <h3 className="text-base font-semibold text-gray-800">Resumo Mensal por Ruta</h3>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                      {availableRutas.map(ruta => (
                        <div key={ruta} className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Ruta {ruta}</p>
                          {macroData.monthlyRutaSummary[ruta] ? (
                            <div className="space-y-1.5">
                              <div>
                                <p className="text-sm font-bold text-blue-600">
                                  {macroData.monthlyRutaSummary[ruta].installmentsReceived}/{macroData.monthlyRutaSummary[ruta].totalInstallments}
                                </p>
                                <p className="text-xs text-gray-400">parcelas</p>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-emerald-500 h-1.5 rounded-full"
                                  style={{ width: `${macroData.monthlyRutaSummary[ruta].totalInstallments > 0 ? (macroData.monthlyRutaSummary[ruta].installmentsReceived / macroData.monthlyRutaSummary[ruta].totalInstallments) * 100 : 0}%` }}
                                />
                              </div>
                              <p className="text-xs font-semibold text-emerald-600">{formatCurrency(macroData.monthlyRutaSummary[ruta].totalReceived)}</p>
                              <p className="text-xs text-gray-500">{formatCurrency(macroData.monthlyRutaSummary[ruta].totalExpected)} esp.</p>
                              <p className="text-xs text-orange-500">{formatCurrency(macroData.monthlyRutaSummary[ruta].totalExpected - macroData.monthlyRutaSummary[ruta].totalReceived)} pend.</p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 py-2">—</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── CUSTOMER LIST MODAL (by date) ── */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="font-bold text-gray-900">Clientes do Dia</h2>
                <p className="text-xs text-gray-400 capitalize">{formatDate(selectedDate)}</p>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={modalSearchTerm}
                  onChange={(e) => setModalSearchTerm(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {getFilteredCustomersForDate(selectedDate)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((customer, index) => {
                  const remainingAmount = customer.amount - (customer.paid_amount || 0);
                  return (
                    <div key={index} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm truncate">{customer.name}</p>
                          <ClientRatingBadge rating={ratings.get(customer.client_id) || null} />
                        </div>
                        <p className="text-xs text-gray-400">Ruta {customer.ruta}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {user?.role === 'manager' ? (
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-800">{formatCurrency(customer.amount)}</p>
                            {customer.paid_amount && customer.paid_amount > 0 && (
                              <p className="text-xs text-emerald-600">Pago: {formatCurrency(customer.paid_amount)}</p>
                            )}
                            {remainingAmount > 0 && (
                              <p className="text-xs text-blue-600">Restante: {formatCurrency(remainingAmount)}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">Parcela a receber</p>
                        )}
                        <button
                          onClick={() => handleCopyClientData(customer.client_id, customer.name)}
                          className="w-8 h-8 rounded-lg bg-gray-700 text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                          title="Copiar dados"
                        >
                          {copiedClientId === customer.client_id
                            ? <CheckCircle2 className="w-3.5 h-3.5" />
                            : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  );
                })}

              {getFilteredCustomersForDate(selectedDate).length === 0 && (
                <div className="py-12 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Nenhum cliente encontrado.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
